import { useMemo } from "react";
import { Text } from "../../types/text";
import { SYSTEM_LANGUAGE_LABELS } from "../../shared/enums/shared-enums";
import { SystemLanguages } from "../../shared/enums/shared-enums";
import { initMetadata } from "../../controllers/formController";
import { SystemEntityProofreadingStatus } from "../../shared/enums/shared-enums";

const BASED_ON_ENUM_MAP: Partial<Record<SystemLanguages, string>> = {
  [SystemLanguages.indic]: "indic_text_based_on",
  [SystemLanguages.tibetan]: "tibetan_text_based_on",
} as const;

const SOURCE_NAME_ENUM_MAP: Partial<Record<SystemLanguages, string>> = {
  [SystemLanguages.indic]: "indic_source_name",
  [SystemLanguages.tibetan]: "tibetan_source_name",
} as const;

const useTextLogic = (language: string | null | undefined) => {
  const systemLanguageType = useMemo<
    Text["system_language_type"] | null
  >(() => {
    if (!Object.values(SystemLanguages).includes(language as SystemLanguages)) {
      return null;
    }
    return language as Text["system_language_type"];
  }, [language]);

  const digitizationEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return "digitization";
  }, [systemLanguageType]);

  const basedOnEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return BASED_ON_ENUM_MAP[systemLanguageType] || "";
  }, [systemLanguageType]);

  const sourceNameEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return SOURCE_NAME_ENUM_MAP[systemLanguageType] || "";
  }, [systemLanguageType]);

  const userEnums = useMemo<string[]>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return [digitizationEnum, basedOnEnum, sourceNameEnum].filter(Boolean);
  }, [systemLanguageType, digitizationEnum, basedOnEnum, sourceNameEnum]);

  const metadataFieldNames = useMemo<Array<keyof Text>>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return ["based_on_type"];
  }, [systemLanguageType]);

  const defaultText = useMemo<Text>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return {
      _id: "",
      digitization: "",
      _key: "",
      _rev: "",
      name: "",
      //size: "",
      based_on_type: "",
      sources: [],
      disabled: false,
      system_language_type: systemLanguageType,
      version: null,
      text: [],
      attributes_metadata: initMetadata<Text>(metadataFieldNames),
      published_id: "",
      text_id: "",
      entity_status_of_proofreading:
        SystemEntityProofreadingStatus.NOT_PROOFREAD,
      notes: "",
      internal_notes: "",
    };
  }, [systemLanguageType, metadataFieldNames]);

  const getDisplayName = (text: Text): string => {
    const systemLanguageLabel =
      SYSTEM_LANGUAGE_LABELS[text.system_language_type];
    const prefix = systemLanguageLabel ? `${systemLanguageLabel} Text` : "Text";
    const displayName =
      (text.name || text.published_id ? `: ` : "") +
      text.published_id +
      (text.published_id && text.name ? " - " : "") +
      text.name;
    return `${prefix}${displayName}`;
  };

  return {
    systemLanguageType,
    userEnums,
    metadataFieldNames,
    defaultText,
    getDisplayName,
    digitizationEnum,
    basedOnEnum,
    sourceNameEnum,
  };
};

export default useTextLogic;
